import { maxBy } from 'es-toolkit';
import React from 'react';
import { SxProps, Theme } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { Component, ComponentDesign, ComponentSide, PointSlopeLine } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { findLine } from '@paragon/relative-geometry';
import { calculateMemberPolygon } from '@paragon/three-shared';
import { notNullish } from '@paragon/utilities';

import { setReferenceLine } from '../../../../store/slices/componentDesignsSlice';
import { useAppDispatch } from '../../../hooks';

function findReferenceLineWithHeelOffset(component: Component, side: ComponentSide): PointSlopeLine | null {
  const topChordGeometries = component.members
    .filter((member) => member.memberType === 'TopChord')
    .map((topChord) => {
      if (!topChord.relativeGeometry) return null;

      const memberPolygon = calculateMemberPolygon(topChord, component);
      if (!memberPolygon) return null;

      const definitionLine = findLine(topChord.relativeGeometry.lineGuid, component);
      if (!definitionLine) return null;

      return { definitionLine, memberPolygon };
    })
    .filter(notNullish);

  const allXs = topChordGeometries.flatMap(({ memberPolygon }) => memberPolygon.vertices.map((vertex) => vertex.x));
  const extremumX = side === 'Left' ? Math.min(...allXs) : Math.max(...allXs);

  const extremaTopChords = topChordGeometries.filter(({ memberPolygon }) =>
    memberPolygon.vertices.some((vertex) => distanceEquals(vertex.x, extremumX)),
  );
  const pointSlopeLinesThatDefineExtremaTopChords = extremaTopChords
    .map(({ definitionLine }) => (definitionLine.lineType === 'PointSlope' ? definitionLine : null))
    .filter(notNullish);

  if (pointSlopeLinesThatDefineExtremaTopChords.length === 0) return null;
  if (pointSlopeLinesThatDefineExtremaTopChords.length === 1) return pointSlopeLinesThatDefineExtremaTopChords[0];

  // Tie-break: Maybe the top chord with the biggest offset will be the actual heel?
  return maxBy(pointSlopeLinesThatDefineExtremaTopChords, (line) => line.offset)!;
}

interface Props {
  readonly componentDesign: ComponentDesign;
  readonly side: ComponentSide;
  readonly sx?: SxProps<Theme>;

  readonly disabled?: boolean;
}

export function HeelHeightMacroInput({ componentDesign, side, ...inputProps }: Props) {
  const dispatch = useAppDispatch();

  const referenceLineWithHeelOffset = findReferenceLineWithHeelOffset(componentDesign.component, side);
  const propertyName = side === 'Left' ? 'Left Heel Height' : 'Right Heel Height';

  return referenceLineWithHeelOffset ? (
    <DistanceField
      {...inputProps}
      label={propertyName}
      value={referenceLineWithHeelOffset.offset}
      onSubmit={(newOffset) => {
        dispatch(
          setReferenceLine({
            componentDesignGuid: componentDesign.guid,
            referenceLine: { ...referenceLineWithHeelOffset, offset: newOffset },
          }),
        );
      }}
      min={0}
    />
  ) : (
    <PropertyTextInput {...inputProps} property={propertyName} value="Not available" disabled />
  );
}
